import PropTypes from 'prop-types';
import TwoClickConfirmButton from '@components/shared/TwoClickConfirmButton';

/**
 * ActionButtons Component
 * Reusable action buttons for image tiles
 */
export default function ImageTileActionButtons({
    isHtmlMode = false,
    isListMode = false,
    hasCrop = false,
    onEdit,
    editTitle = 'Edit',
    onLightbox,
    onReplace,
    onCrop,
    onRegenerate,
    onRemove,
    removeTitle = 'Delete',
    hideNarrow = true,
    replaceInputRef,
    onReplaceFileChange,
}) {
    const containerClasses = isListMode
        ? "flex items-center gap-1"
        : "absolute top-1 right-1 flex items-center gap-1 !opacity-100 z-20 pointer-events-auto";

    const handleClick = (e) => {
        e.stopPropagation();
    };
    
    const handlePointerDown = (e) => {
        e.stopPropagation();
    };

    return (
        <div 
            className={containerClasses} 
            onClick={handleClick}
            onPointerDown={handlePointerDown}
            onMouseDown={handlePointerDown}
            data-action-button
        >
            <input
                type="file"
                ref={replaceInputRef}
                style={{ display: 'none' }}
                accept="image/jpeg,image/jpg"
                onChange={onReplaceFileChange}
            />
            {onEdit && (
                <button
                    type="button"
                    tabIndex="-1"
                    onClick={onEdit}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    title={editTitle}
                    aria-label={editTitle}
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                </button>
            )}
            {onLightbox && (
                <button
                    tabIndex="-1"
                    onClick={onLightbox}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Preview"
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                </button>
            )}
            {onReplace && (
                <button
                    tabIndex="-1"
                    onClick={onReplace}
                    className={`p-1 text-gray-400 hover:text-gray-600 transition-colors ${hideNarrow ? 'max-sm:hidden' : ''}`}
                    title="Replace image"
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                </button>
            )}
            {onCrop && !isHtmlMode && (
                <button
                    tabIndex="-1"
                    onClick={onCrop}
                    className={`p-1 transition-colors ${hideNarrow ? 'max-sm:hidden' : ''} ${hasCrop ? 'text-purple-600 hover:text-purple-700' : 'text-gray-400 hover:text-gray-600'}`}
                    title={hasCrop ? "Edit crop" : "Crop image"}
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 3v4M3 7h4m10 0h4M17 3v4M7 17v4m-4-4h4m10 0h4m-4-4v4M7 7h10v10H7z" />
                    </svg>
                </button>
            )}
            {onRegenerate && (
            <button
                tabIndex="-1"
                onClick={onRegenerate}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title="Regenerate"
            >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
            </button>
            )}
            {onRemove && (
                <TwoClickConfirmButton
                    tabIndex={-1}
                    onConfirm={onRemove}
                    onBeforeToggle={handleClick}
                    armTimeoutMs={4000}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    confirmClassName="p-1 text-red-600 hover:text-red-700 transition-colors"
                    title={removeTitle}
                    confirmTitle="Click again to confirm"
                    ariaLabel={removeTitle}
                    confirmAriaLabel="Confirm delete"
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                </TwoClickConfirmButton>
            )}
        </div>
    );
}

ImageTileActionButtons.propTypes = {
    isHtmlMode: PropTypes.bool,
    isListMode: PropTypes.bool,
    hasCrop: PropTypes.bool,
    onEdit: PropTypes.func,
    editTitle: PropTypes.string,
    onLightbox: PropTypes.func,
    onReplace: PropTypes.func,
    onCrop: PropTypes.func,
    onRegenerate: PropTypes.func,
    onRemove: PropTypes.func,
    removeTitle: PropTypes.string,
    hideNarrow: PropTypes.bool,
    replaceInputRef: PropTypes.object,
    onReplaceFileChange: PropTypes.func,
};